import {
  EventType,
  InteractionRequiredAuthError,
  type AccountInfo,
  type AuthenticationResult,
  type EventMessage,
} from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { AuthenticatedUserSchema, type AuthenticatedUser } from '@dcc-erp/shared';

import { API_BASE } from '../api';
import { ENTRA_API_SCOPE, loginRequest, msalInstance, tokenRequestScopes } from './msal-config';
import { saveCurrentPathForReturn } from './return-path';
import { ACTIVE_COMPANY_STORAGE_KEY, AuthContext, type AuthContextValue } from './useAuth';

interface AuthProviderProps {
  children: ReactNode;
}

// Dev-only bypass for Playwright E2E. Two conditions are required:
//   1. The build was started with VITE_E2E_BYPASS_MSAL=true (compile-time)
//   2. The window has localStorage['dcc-erp/e2e-bypass'] === 'true' (runtime)
//
// The two-key gate keeps the unauthed login.spec.ts tests honest — they
// never set the localStorage flag, so AuthProvider falls through to the
// real MSAL flow even when the bypass env is on. Authed-route specs
// (dashboard / journal / master-data) flip the localStorage flag via
// authedPage() before each navigation.
//
// When BOTH gates are open, AuthProvider skips MSAL entirely and exposes a
// fixed AuthenticatedUser via the context — useful so Playwright tests can
// stub the API layer with route() instead of driving the real Entra OAuth flow.
// NEVER ship this flag enabled in production. Backend auth is NOT bypassed —
// for API-touching tests, mock /api/v1/* responses with page.route().
const E2E_BYPASS_BUILD_FLAG = import.meta.env.VITE_E2E_BYPASS_MSAL === 'true';
const E2E_BYPASS_STORAGE_KEY = 'dcc-erp/e2e-bypass';

function isE2EBypassActive(): boolean {
  if (!E2E_BYPASS_BUILD_FLAG) return false;
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(E2E_BYPASS_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

const E2E_MOCK_COMPANY_ID = '67b118ca-f3b9-417a-b89d-dbb55e5149e6'; // DCC seed UUID
const E2E_MOCK_USER: AuthenticatedUser = {
  id: 'e2e-test-user',
  entraOid: '00000000-0000-0000-0000-000000000099',
  email: 'e2e@dynastyceramic.com',
  preferredUsername: 'e2e@dynastyceramic.com',
  displayName: 'E2E Test User',
  defaultCompanyId: E2E_MOCK_COMPANY_ID,
  allowedCompanyIds: [E2E_MOCK_COMPANY_ID],
};

/**
 * Top-level provider. Wraps `<MsalProvider>` and exposes the merged auth state
 * (MSAL account + `/auth/me` shape + active-company selection) via `useAuth()`.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  if (isE2EBypassActive()) {
    // No MsalProvider — AuthStateProvider's bypass branch short-circuits
    // before any useMsal() call.
    return <AuthStateProvider>{children}</AuthStateProvider>;
  }
  return (
    <MsalProvider instance={msalInstance}>
      <AuthStateProvider>{children}</AuthStateProvider>
    </MsalProvider>
  );
}

function AuthStateProvider({ children }: { children: ReactNode }) {
  if (isE2EBypassActive()) {
    return <E2EBypassAuthProvider>{children}</E2EBypassAuthProvider>;
  }
  return <MsalAuthStateProvider>{children}</MsalAuthStateProvider>;
}

/**
 * E2E-only AuthProvider body. Skips MSAL, /auth/me, and active-company
 * reconciliation — just hands the rest of the app a fixed user + DCC active
 * company so route guards pass and queries can fire against mocked APIs.
 */
function E2EBypassAuthProvider({ children }: { children: ReactNode }) {
  const value: AuthContextValue = useMemo(
    () => ({
      user: E2E_MOCK_USER,
      isAuthenticated: true,
      isLoading: false,
      activeCompanyId: E2E_MOCK_USER.defaultCompanyId,
      setActiveCompanyId: () => undefined,
      // These return Promise.resolve(...) (not `async () => ...`) to keep
      // the eslint `require-await` rule happy without an inline disable —
      // the AuthContextValue signature requires `() => Promise<...>`.
      login: () => Promise.resolve(),
      logout: () => Promise.resolve(),
      getAccessToken: () => Promise.resolve('e2e-fake-token'),
    }),
    [],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function MsalAuthStateProvider({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const [msalReady, setMsalReady] = useState(false);
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [userLoading, setUserLoading] = useState(false);
  const [activeCompanyId, setActiveCompanyIdState] = useState<string | null>(null);
  const fetchedForOidRef = useRef<string | null>(null);

  // 1. Resolve MSAL init + redirect promise once on mount.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        await instance.initialize();
        console.log('[auth] msal initialized; processing redirect…');
        // `navigateToLoginRequestUrl` defaults to TRUE in @azure/msal-browser v5
        // (RedirectClient does `options?.navigateToLoginRequestUrl ?? true`). With the
        // default, once the Entra response hash is detected at the `/auth/callback`
        // redirectUri, MSAL hard-navigates the browser back to the page that *started*
        // the login (always `/login`) to finish processing there — so every sign-in
        // visibly bounced `/auth/callback` → `/login` (the flash) → `/dashboard`.
        // We own post-login routing in the `/auth/callback` route itself
        // (`returnTo ?? /dashboard`), so disable MSAL's auto-bounce and let
        // handleRedirectPromise resolve the response in place.
        const response = await instance.handleRedirectPromise({
          navigateToLoginRequestUrl: false,
        });
        if (cancelled) return;
        console.log('[auth] handleRedirectPromise resolved', {
          gotAccount: !!response?.account,
          account: response?.account?.username,
          inCache: instance.getAllAccounts().length,
        });
        if (response?.account) {
          instance.setActiveAccount(response.account);
        } else {
          const existing = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
          if (existing) {
            instance.setActiveAccount(existing);
            console.log('[auth] restored cached account', existing.username);
          }
        }
      } catch (err) {
        console.error('[auth] MSAL initialization failed', err);
      } finally {
        if (!cancelled) setMsalReady(true);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [instance]);

  // 2. Keep active account in sync with login-success events.
  useEffect(() => {
    const callbackId = instance.addEventCallback((event: EventMessage) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
      ) {
        const payload = event.payload as AuthenticationResult | null;
        if (payload?.account) {
          instance.setActiveAccount(payload.account);
        }
      }
    });
    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance]);

  const account: AccountInfo | null = useMemo(() => {
    return instance.getActiveAccount() ?? accounts[0] ?? null;
  }, [accounts, instance]);

  // 3. Fetch /auth/me whenever we acquire (or change) an MSAL account.
  useEffect(() => {
    if (!msalReady) return;
    if (!account) {
      // Defer to a microtask so we don't synchronously trigger a cascading render
      // from inside the effect body (see react-hooks/set-state-in-effect rule).
      // Functional updater short-circuits when prev is already null, keeping the
      // effect idempotent across StrictMode double-invocations.
      queueMicrotask(() => {
        setUser((prev) => (prev === null ? prev : null));
        fetchedForOidRef.current = null;
      });
      return;
    }
    const oid =
      (account.idTokenClaims as { oid?: string } | undefined)?.oid ?? account.homeAccountId;
    if (fetchedForOidRef.current === oid && user) return;

    let cancelled = false;
    const run = async () => {
      setUserLoading(true);
      try {
        const tokenResult = await instance.acquireTokenSilent({
          account,
          scopes: tokenRequestScopes,
        });
        if (cancelled) return;
        const res = await fetch(`${API_BASE}/auth/me`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${tokenResult.accessToken}`,
          },
        });
        if (!res.ok) {
          throw new Error(`/auth/me failed with ${res.status}`);
        }
        const json: unknown = await res.json();
        const parsed = AuthenticatedUserSchema.parse(json);
        if (cancelled) return;
        setUser(parsed);
        fetchedForOidRef.current = oid;
      } catch (err) {
        if (cancelled) return;
        if (err instanceof InteractionRequiredAuthError) {
          // Preserve the current page across the interactive re-auth bounce.
          saveCurrentPathForReturn();
          void instance.acquireTokenRedirect({ scopes: tokenRequestScopes });
          return;
        }
        console.error('[auth] failed to load /auth/me', err);
        setUser(null);
      } finally {
        if (!cancelled) setUserLoading(false);
      }
    };
    void run();

    return () => {
      cancelled = true;
    };
  }, [account, instance, msalReady, user]);

  // 4. Reconcile active-company once `user` is loaded.
  useEffect(() => {
    if (!user) {
      // Same deferred-update pattern as above to keep the effect body free of
      // synchronous state updates (react-hooks/set-state-in-effect).
      queueMicrotask(() => {
        setActiveCompanyIdState((prev) => (prev === null ? prev : null));
      });
      return;
    }
    const stored =
      typeof window !== 'undefined'
        ? window.localStorage.getItem(ACTIVE_COMPANY_STORAGE_KEY)
        : null;
    let next: string | null;
    if (stored && user.allowedCompanyIds.includes(stored)) {
      next = stored;
    } else if (user.allowedCompanyIds.includes(user.defaultCompanyId)) {
      next = user.defaultCompanyId;
    } else {
      next = user.allowedCompanyIds[0] ?? null;
    }
    queueMicrotask(() => {
      setActiveCompanyIdState((prev) => (prev === next ? prev : next));
    });
  }, [user]);

  const setActiveCompanyId = useCallback(
    (companyId: string) => {
      if (!user) {
        throw new Error('Cannot set active company before user is loaded');
      }
      if (!user.allowedCompanyIds.includes(companyId)) {
        throw new Error(`Company ${companyId} is not in allowedCompanyIds`);
      }
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(ACTIVE_COMPANY_STORAGE_KEY, companyId);
      }
      setActiveCompanyIdState(companyId);
    },
    [user],
  );

  const login = useCallback(async () => {
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(async () => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(ACTIVE_COMPANY_STORAGE_KEY);
    }
    await instance.logoutRedirect({
      account,
      postLogoutRedirectUri: `${window.location.origin}/login`,
    });
  }, [account, instance]);

  const getAccessToken = useCallback(async () => {
    const acct = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!acct) {
      throw new Error('No signed-in account');
    }
    try {
      const result = await instance.acquireTokenSilent({
        account: acct,
        scopes: [ENTRA_API_SCOPE],
      });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Preserve the current page across the interactive re-auth bounce.
        saveCurrentPathForReturn();
        void instance.acquireTokenRedirect({ scopes: [ENTRA_API_SCOPE] });
        // Browser navigates away — never resolves.
        return await new Promise<string>(() => undefined);
      }
      throw err;
    }
  }, [instance]);

  const isAuthenticated = !!account && !!user;
  // Once MSAL hands us an account, we MUST be considered "loading" until /me
  // resolves — otherwise there's a 1-frame gap where isAuthenticated=false +
  // isLoading=false, which makes RequireAuth bounce the user back to /login.
  const isLoading = !msalReady || inProgress !== 'none' || (!!account && !user);

  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    console.log('[auth]', {
      msalReady,
      inProgress,
      hasAccount: !!account,
      hasUser: !!user,
      userLoading,
      isAuthenticated,
      isLoading,
    });
  }

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      activeCompanyId,
      setActiveCompanyId,
      login,
      logout,
      getAccessToken,
    }),
    [
      user,
      isAuthenticated,
      isLoading,
      activeCompanyId,
      setActiveCompanyId,
      login,
      logout,
      getAccessToken,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
